import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { firmwareAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { Upload, File, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { hasAdminPermission } from '../utils/adminPermissions';

export default function FirmwareUpload() {
  const { t } = useTranslation();
  const { user } = useAuthStore();

  const hasPermission = hasAdminPermission(user, 'manage_firmware');
  const [file, setFile] = useState(null);
  const [version, setVersion] = useState('');
  const [description, setDescription] = useState('');
  const [deviceModel, setDeviceModel] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [message, setMessage] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (selectedFile) => {
    if (!/\.(bin|hex|fw)$/i.test(selectedFile.name)) {
      setMessage({
        type: 'error',
        text: t('firmware.upload.fileTypeInvalid', { defaultValue: '仅允许 .bin、.hex 或 .fw 固件文件' })
      });
      return;
    }
    if (selectedFile.size > 50 * 1024 * 1024) {
      setMessage({ type: 'error', text: t('firmware.upload.fileSizeLimit') });
      return;
    }
    setFile(selectedFile);
    setVersion(current => current || selectedFile.name);
    setMessage(null);
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setMessage({ type: 'error', text: t('firmware.upload.fileRequired') });
      return;
    }
    if (!version) {
      setMessage({ type: 'error', text: t('firmware.upload.versionRequired') });
      return;
    }
    if (!deviceModel) {
      setMessage({ type: 'error', text: t('firmware.upload.modelRequired') });
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setMessage(null);

    try {
      const formData = new FormData();
      formData.append('firmware', file);
      formData.append('version', version);
      formData.append('description', description);
      formData.append('deviceModel', deviceModel);

      const response = await firmwareAPI.uploadFirmware(formData, (progressEvent) => {
        if (!progressEvent.total) return;
        setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
      });

      if (response.data?.success) {
        setMessage({ type: 'success', text: t('firmware.upload.uploadSuccess') });
        // Reset form
        setFile(null);
        setVersion('');
        setDescription('');
        setDeviceModel('');
        setUploadProgress(100);
      } else {
        setMessage({ type: 'error', text: t('firmware.upload.uploadFailed') });
      }
    } catch (error) {
      console.error('[Firmware Upload] Error:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || t('firmware.upload.uploadFailed')
      });
    } finally {
      setUploading(false);
    }
  };

  if (!hasPermission) return null;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">{t('firmware.upload.title')}</h1>
        <p className="text-slate-400 mt-1">{t('firmware.upload.subtitle')}</p>
      </div>

      <div className="bg-slate-800 border border-white/10 rounded-xl p-6 space-y-6">
        {/* File Drop Zone */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            {t('firmware.upload.selectFile')}
          </label>
          <div
            className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
              dragActive
                ? 'border-cyan-400 bg-cyan-400/5'
                : 'border-white/10 hover:border-white/20'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              id="firmware-file"
              className="hidden"
              accept=".bin,.hex,.fw"
              onChange={handleFileInput}
              disabled={uploading}
            />
            <label
              htmlFor="firmware-file"
              className="cursor-pointer flex flex-col items-center"
            >
              {file ? (
                <>
                  <File className="w-12 h-12 text-cyan-400 mb-3" />
                  <p className="text-white font-medium">{file.name}</p>
                  <p className="text-slate-400 text-sm mt-1">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </>
              ) : (
                <>
                  <Upload className="w-12 h-12 text-slate-400 mb-3" />
                  <p className="text-white font-medium mb-1">
                    {t('firmware.upload.dragDrop')}
                  </p>
                  <p className="text-slate-400 text-sm">
                    {t('firmware.upload.fileSizeLimit')}
                  </p>
                </>
              )}
            </label>
          </div>
        </div>

        {/* Version Input */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            {t('firmware.upload.version')}
          </label>
          <input
            type="text"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            placeholder={t('firmware.upload.versionPlaceholder')}
            className="w-full bg-slate-900 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400"
            disabled={uploading}
          />
        </div>

        {/* Device Model Selector */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            {t('firmware.upload.deviceModel')}
          </label>
          <select
            value={deviceModel}
            onChange={(e) => setDeviceModel(e.target.value)}
            className="w-full bg-slate-900 border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-cyan-400"
            disabled={uploading}
          >
            <option value="">{t('firmware.upload.modelPlaceholder')}</option>
            <option value="ATM-ID-1000P">ATM-ID-1000P</option>
          </select>
        </div>

        {/* Description Textarea */}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            {t('firmware.upload.description')}
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('firmware.upload.descriptionPlaceholder')}
            rows={4}
            className="w-full bg-slate-900 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400 resize-none"
            disabled={uploading}
          />
        </div>

        {/* Upload Progress */}
        {uploading && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">{t('firmware.upload.uploading')}</span>
              <span className="text-cyan-400">{uploadProgress}%</span>
            </div>
            <div className="w-full bg-slate-900 rounded-full h-2 overflow-hidden">
              <div
                className="bg-cyan-400 h-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Message */}
        {message && (
          <div
            className={`flex items-center gap-3 p-4 rounded-lg ${
              message.type === 'success'
                ? 'bg-emerald-400/10 border border-emerald-400/20'
                : 'bg-rose-400/10 border border-rose-400/20'
            }`}
          >
            {message.type === 'success' ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
            ) : (
              <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0" />
            )}
            <p
              className={`text-sm ${
                message.type === 'success' ? 'text-emerald-400' : 'text-rose-400'
              }`}
            >
              {message.text}
            </p>
          </div>
        )}

        {/* Upload Button */}
        <button
          onClick={handleUpload}
          disabled={uploading || !file || !version || !deviceModel}
          className="w-full bg-cyan-400 text-slate-900 font-medium py-3 rounded-lg hover:bg-cyan-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {uploading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {t('firmware.upload.uploading')}
            </>
          ) : (
            <>
              <Upload className="w-5 h-5" />
              {t('firmware.upload.uploadBtn')}
            </>
          )}
        </button>
      </div>
    </div>
  );
}
